import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ListChecks, Loader2, Sparkles, Image as ImageIcon, FileText, Library, Trash2, Brain } from 'lucide-react';
import SmartQueue from '@/components/SmartQueue';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

const STAGE_STYLES = {
  identified: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  title_abstract: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  fulltext: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  included: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  excluded: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300',
};
const STAGE_LABEL = {
  identified: 'not screened', title_abstract: 'unclear', fulltext: 'full text',
  included: 'included', excluded: 'excluded',
};

export default function ScreeningPanel({ userId, projectId, pendingRecords, onConsumedRecords,
  onAssetsChanged, onIngested }) {
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: 'Screening', question: '', inclusion: '', exclusion: '' });
  const [flowKey, setFlowKey] = useState(0);
  const [paragraph, setParagraph] = useState('');
  const [smart, setSmart] = useState(false);
  const pollRef = useRef(null);

  const loadSessions = useCallback(async () => {
    try { setSessions(await thesisApi.screeningSessions(userId, projectId)); } catch (e) { console.error(e); }
  }, [userId, projectId]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const openSession = useCallback(async (id) => {
    try { setActive(await thesisApi.getScreeningSession(userId, id)); setFlowKey((n) => n + 1); }
    catch (e) { toast.error(e.message); }
  }, [userId]);

  useEffect(() => {
    if (active?.ai_status !== 'running') { clearInterval(pollRef.current); return undefined; }
    pollRef.current = setInterval(() => openSession(active.id), 5000);
    return () => clearInterval(pollRef.current);
  }, [active?.ai_status, active?.id, openSession]);

  const criteria = (text) => text.split(/[\n;]+/).map((s) => s.trim()).filter(Boolean);

  const createSession = async () => {
    if (!pendingRecords?.length) { toast.error('Run a literature search first, then “Send to PRISMA screening”'); return; }
    setBusy(true);
    try {
      const s = await thesisApi.createScreeningSession(userId, {
        project_id: projectId, name: form.name || 'Screening', question: form.question,
        inclusion: criteria(form.inclusion), exclusion: criteria(form.exclusion),
        records: pendingRecords,
      });
      toast.success(`Session created — ${s.records_added} records, ${s.duplicates_removed} duplicates removed`);
      setCreating(false);
      onConsumedRecords?.();
      await loadSessions();
      await openSession(s.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const addPending = async () => {
    setBusy(true);
    try {
      const res = await thesisApi.addScreeningRecords(userId, active.id, pendingRecords);
      toast.success(`Added ${res.added} records · ${res.duplicates_removed} duplicates`);
      onConsumedRecords?.();
      await openSession(active.id);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const runAi = async () => {
    setBusy(true);
    try {
      await thesisApi.aiScreen(userId, active.id);
      toast.success('AI screening started — decisions appear as they land');
      await openSession(active.id);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const setStage = async (recordId, stage) => {
    try {
      await thesisApi.updateScreeningRecord(userId, recordId, { stage, reason: '' });
      await openSession(active.id);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const saveFigure = async () => {
    setBusy(true);
    try {
      const f = await thesisApi.saveScreeningFigure(userId, active.id);
      toast.success(`Flow diagram saved as Figure ${f.number}`);
      onAssetsChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const loadParagraph = async () => {
    try {
      const res = await thesisApi.screeningMethodsParagraph(userId, active.id);
      setParagraph(res.paragraph + '\n' + res.table);
    } catch (e) { toast.error(e.message); }
  };

  const ingestIncluded = async () => {
    setBusy(true);
    try {
      const res = await thesisApi.ingestIncludedStudies(userId, active.id);
      toast.success(`Added ${res.ingested} included studies to the library`);
      onIngested?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'object' ? d.error : (d || e.message));
    } finally { setBusy(false); }
  };

  const removeSession = async (id) => {
    await thesisApi.deleteScreeningSession(userId, id);
    if (active?.id === id) setActive(null);
    await loadSessions();
  };

  const c = active?.counts || {};

  return (
    <Card data-testid="screening-panel">
      <CardHeader>
        <CardTitle className="font-serif flex flex-wrap items-center gap-2" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
          <ListChecks className="w-5 h-5 text-[#1d4ed8]" /> PRISMA 2020 screening
          <Badge variant="outline" className="text-[10px]">dedupe · AI title/abstract · flow diagram</Badge>
        </CardTitle>
        <CardDescription>
          Turn a search into a defensible selection process: duplicates removed on entry, AI proposes
          include/exclude with a reason for every record, you decide, and the flow diagram plus the
          study-selection paragraph are generated from the real numbers.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          {sessions.map((s) => (
            <Button key={s.id} size="sm" variant={active?.id === s.id ? 'default' : 'outline'}
              onClick={() => openSession(s.id)} data-testid={`screening-session-${s.id}`}>
              {s.name} · {s.counts?.screened || 0}
            </Button>
          ))}
          <Button size="sm" variant="outline" onClick={() => setCreating((v) => !v)}
            data-testid="screening-new-btn">
            New session {pendingRecords?.length ? `(${pendingRecords.length} records ready)` : ''}
          </Button>
          {active && (
            <Button size="sm" variant="ghost" onClick={() => removeSession(active.id)}
              data-testid="screening-delete-btn"><Trash2 className="w-4 h-4" /></Button>
          )}
        </div>

        {creating && (
          <div className="space-y-2 p-3 rounded-md border border-border" data-testid="screening-new-form">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Session name" data-testid="screening-name-input" />
            <Textarea value={form.question} rows={2} data-testid="screening-question-input"
              onChange={(e) => setForm({ ...form, question: e.target.value })}
              placeholder="Review question (PICO): In adults with …, does … compared with … affect …?" />
            <Textarea value={form.inclusion} rows={3} data-testid="screening-inclusion-input"
              onChange={(e) => setForm({ ...form, inclusion: e.target.value })}
              placeholder={'Inclusion criteria — one per line'} />
            <Textarea value={form.exclusion} rows={3} data-testid="screening-exclusion-input"
              onChange={(e) => setForm({ ...form, exclusion: e.target.value })}
              placeholder={'Exclusion criteria — one per line'} />
            <Button onClick={createSession} disabled={busy} data-testid="screening-create-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Create from {pendingRecords?.length || 0} search records
            </Button>
          </div>
        )}

        {active && (
          <div className="space-y-3" data-testid="screening-active">
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" onClick={runAi} disabled={busy || active.ai_status === 'running'}
                data-testid="screening-ai-btn">
                {active.ai_status === 'running'
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Screening {active.ai_progress || 0}/{active.ai_total || 0}</>
                  : <><Sparkles className="w-4 h-4 mr-2" />AI screen unscreened</>}
              </Button>
              {!!pendingRecords?.length && (
                <Button size="sm" variant="outline" onClick={addPending} disabled={busy}
                  data-testid="screening-add-records-btn">Add {pendingRecords.length} new records</Button>
              )}
              <Button size="sm" variant={smart ? 'default' : 'outline'}
                onClick={() => setSmart((v) => !v)} data-testid="screening-smart-queue-btn">
                <Brain className="w-4 h-4 mr-2" /> {smart ? 'Hide smart queue' : 'Smart queue'}
              </Button>
              <Button size="sm" variant="outline" onClick={saveFigure} disabled={busy}
                data-testid="screening-save-figure-btn">
                <ImageIcon className="w-4 h-4 mr-2" /> Save flow diagram as figure
              </Button>
              <Button size="sm" variant="outline" onClick={loadParagraph}
                data-testid="screening-paragraph-btn">
                <FileText className="w-4 h-4 mr-2" /> Study-selection paragraph
              </Button>
              <Button size="sm" variant="outline" onClick={ingestIncluded} disabled={busy}
                data-testid="screening-ingest-btn">
                <Library className="w-4 h-4 mr-2" /> Add included to library
              </Button>
            </div>
            {active.ai_error && <p className="text-[11px] text-rose-600">AI screening failed: {active.ai_error}</p>}

            {smart && (
              <SmartQueue userId={userId} sessionId={active.id} question={active.question}
                onDecided={() => openSession(active.id)} />
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center" data-testid="screening-counts">
              {[['Identified', c.identified], ['Duplicates', c.duplicates_removed],
                ['Excluded (T/A)', c.ta_excluded], ['Full text', c.fulltext_assessed],
                ['Included', c.included], ['Pending', c.pending]].map(([label, value]) => (
                <div key={label} className="p-2 rounded-md border border-border">
                  <div className="text-lg font-semibold">{value ?? 0}</div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
                </div>
              ))}
            </div>

            <img key={flowKey} src={thesisApi.screeningFlowUrl(userId, active.id)}
              alt="PRISMA flow diagram" className="w-full max-w-md mx-auto rounded-md border border-border"
              data-testid="screening-flow-image" />

            {paragraph && (
              <pre className="text-[11px] bg-muted/40 rounded p-2 overflow-x-auto max-h-56 whitespace-pre-wrap"
                data-testid="screening-paragraph">{paragraph}</pre>
            )}

            <div className="max-h-[460px] overflow-y-auto space-y-2 pr-1">
              {(active.records || []).map((r) => (
                <div key={r.id} className="p-2 rounded-md border border-border" data-testid={`screening-record-${r.id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${STAGE_STYLES[r.stage]}`}>
                      {STAGE_LABEL[r.stage]}
                    </span>
                    {r.decided_by === 'ai' && <Badge variant="outline" className="text-[9px]">AI</Badge>}
                    {r.decided_by === 'author' && <Badge variant="outline" className="text-[9px]">you</Badge>}
                    <span className="text-sm font-medium leading-snug min-w-0">{r.title}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    {r.journal} {r.year} {r.doi ? `· doi:${r.doi}` : ''}
                  </div>
                  {r.reason && <div className="text-[11px] italic mt-1">{r.reason}</div>}
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {['fulltext', 'included', 'excluded'].map((stage) => (
                      <Button key={stage} size="sm" variant="ghost"
                        className="h-6 px-2 text-[10px]"
                        onClick={() => setStage(r.id, stage)}
                        data-testid={`screening-set-${stage}-${r.id}`}>
                        {stage === 'fulltext' ? '→ full text' : stage === 'included' ? '✓ include' : '✕ exclude'}
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
